import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, Check, PenLine, Link2, Gift, MessageCircle,
} from 'lucide-react';
import { PageShell } from '@/components/brand/PageShell';
import { PageHeader } from '@/components/brand/PageHeader';
import { BrandButton } from '@/components/brand/Buttons';
import { LogoMark } from '@/components/brand/Logo';
import { HIW_HERO, imgProps } from '@/lib/imagery';

/* -------------------------------------------------------------------------- */
/*  Content                                                                    */
/* -------------------------------------------------------------------------- */

const steps = [
  {
    n: '01',
    icon: PenLine,
    eyebrow: 'A quiet beginning',
    title: 'Tell us your due date.',
    body:
      "It's the only thing we really need to start. From there, we set the table — what you'll need by the third trimester, the first weeks at home, the months that follow.",
  },
  {
    n: '02',
    icon: Link2,
    eyebrow: 'Curate, your way',
    title: 'Add anything you love.',
    body:
      "Curated picks from brands you can trust, plus a 'paste a link' button for any shop on the internet. No walled garden. No nudge to overspend.",
  },
  {
    n: '03',
    icon: Gift,
    eyebrow: 'Share gently',
    title: 'A single, beautiful link.',
    body:
      "Send it when you're ready. Friends and family see exactly what you'd love — and can chip in together for the bigger, heirloom things.",
  },
  {
    n: '04',
    icon: MessageCircle,
    eyebrow: 'Receive, with grace',
    title: 'No duplicates. Ever.',
    body:
      "We mark each gift the moment it's claimed, so two friends never buy the same stroller. And you'll always know who gave what — which makes the thank-yous easy.",
  },
];

const promises = [
  ['Free, always',                'For you and every guest. We earn from partner brands, not you.'],
  ['Any brand, any shop',         'Add from anywhere on the internet. No walled garden.'],
  ['Group gifting, built in',     'Friends can chip in toward big-ticket items, with a personal note.'],
  ['Start at any trimester',      'Save now, share when ready. Your basket stays private until you decide.'],
  ['Made in India, for India',    'Pricing in ₹, the brands you actually use, delivery to your door.'],
  ['Privacy by default',          'Your registry is never indexed by search. Only people with the link see it.'],
];

/* -------------------------------------------------------------------------- */
/*  Page                                                                       */
/* -------------------------------------------------------------------------- */

const HowItWorks = () => {
  const navigate = useNavigate();

  return (
    <PageShell stickyMobileCTA>
      {/* Hero — one photo, emotional anchor */}
      <section className="relative pt-16 sm:pt-24 pb-20">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-20 right-0 h-[460px] w-[460px] rounded-full blur-3xl opacity-50"
          style={{ background: 'radial-gradient(closest-side, #F4D6CD 0%, transparent 70%)' }}
        />
        <div className="container-page relative">
          <PageHeader
            eyebrow="How Joy Basket works"
            title="Four small steps."
            titleAccent="Then, breathe."
            lede="A registry that meets you where you are — and grows with your little one. Free, private until you're ready, and built around new parents."
            size="xl"
          />

          <div className="mt-14 relative aspect-[16/8] sm:aspect-[16/7] rounded-[2rem] overflow-hidden ring-1 ring-black/5 shadow-soft-2">
            <img {...imgProps(HIW_HERO)} className="h-full w-full object-cover" loading="eager" />
          </div>
        </div>
      </section>

      {/* Steps — icon-led, mirrors the homepage rhythm */}
      <section className="pb-24 sm:pb-32">
        <div className="container-page">
          <ol className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-14 sm:gap-y-16">
            {steps.map((s) => {
              const Icon = s.icon;
              return (
                <li key={s.n} className="relative">
                  <div className="flex items-center gap-4 mb-5">
                    <div className="h-14 w-14 rounded-full bg-clay-600 text-cream-50 flex items-center justify-center shadow-soft-1">
                      <Icon className="h-6 w-6" />
                    </div>
                    <span className="font-display text-2xl text-clay-600/70 tracking-tight">
                      {s.n}
                    </span>
                    <span className="hairline flex-1" />
                  </div>
                  <div className="eyebrow mb-3">{s.eyebrow}</div>
                  <h2 className="font-display text-display-md text-ink leading-snug">
                    {s.title}
                  </h2>
                  <p className="mt-4 text-[17px] text-ink-soft leading-relaxed max-w-md">
                    {s.body}
                  </p>
                </li>
              );
            })}
          </ol>
        </div>
      </section>

      {/* Promises */}
      <section className="py-24 bg-cream-100/60 border-y border-border/60">
        <div className="container-page">
          <div className="max-w-2xl mb-12">
            <div className="eyebrow mb-4">What we promise</div>
            <h2 className="font-display text-display-lg text-ink">
              Built around new parents, <span className="italic text-clay-700">not advertisers.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-7">
            {promises.map(([title, body]) => (
              <div key={title} className="flex gap-4">
                <span className="mt-1 inline-flex shrink-0 h-7 w-7 rounded-full bg-clay-100 text-clay-700 items-center justify-center">
                  <Check className="h-3.5 w-3.5" />
                </span>
                <div>
                  <p className="font-display text-lg text-ink leading-snug">{title}</p>
                  <p className="mt-1 text-sm text-ink-soft leading-relaxed">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24">
        <div className="container-page">
          <div className="rounded-[2rem] bg-clay-700 text-cream-50 p-10 sm:p-16 text-center relative overflow-hidden">
            <div
              aria-hidden
              className="absolute inset-0 opacity-[0.06]"
              style={{
                backgroundImage:
                  'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.6) 1px, transparent 0)',
                backgroundSize: '28px 28px',
              }}
            />
            <div className="relative">
              <LogoMark size={28} className="text-cream-50 mx-auto mb-6" />
              <h2 className="font-display text-display-xl max-w-2xl mx-auto">
                Begin your basket.{' '}
                <span className="italic">It takes two minutes.</span>
              </h2>
              <p className="mt-5 text-cream-100/85 max-w-lg mx-auto">
                Free forever for you and your guests. Start private, share when you're ready.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                <BrandButton
                  size="lg"
                  variant="cream"
                  onClick={() => navigate('/registry/new')}
                >
                  Create Your Registry
                  <ArrowRight className="h-4 w-4" />
                </BrandButton>
                <BrandButton
                  size="lg"
                  variant="ghost"
                  onClick={() => navigate('/explore-gifts')}
                  className="!text-cream-50 hover:!bg-cream-50/10 hover:!text-cream-50"
                >
                  Explore Gifts
                </BrandButton>
              </div>
            </div>
          </div>
        </div>
      </section>
    </PageShell>
  );
};

export default HowItWorks;
